import { Module, VuexModule, Mutation, Action, getModule } from "vuex-module-decorators";
import { formatBool, formatDate } from "../../../../utils";
import { TableField, PageData } from "../../../types";
import {
    GetStockOrdersRequest,
    StockOrderListFilters,
    StockOrderResource,
    StockOrderListItem,
    StockOrderItemListItem
  } from "../../../../models/api/barcodeSystem/stockOrders/get";
import Store from "../../../index";
import { rawApi, api } from "../../../../lib";
import { AxiosResponse } from "axios";
import { defaultStockOrder, defaultStockOrderFilters } from "./helpers";
import BarcodeSystemModule from "@/store/modules/barcodeSystem";
import notifications from "@/lib/notifications";
import _ from "lodash";
import * as modals from "@/lib/modals";
import { ReceiveStockOrderRequest } from "../../../../models/api/barcodeSystem/stockOrders/receive";
import { CreateStockOrderRequest } from "../../../../models/api/barcodeSystem/stockOrders/create";

const name = "stockOrder";
const barcodeSystemModuleState = getModule(BarcodeSystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class StockOrderModule extends VuexModule {
    public currentStockOrder: StockOrderResource = defaultStockOrder();
    public currentStockOrderItems: StockOrderItemListItem[] = null;
    public data?: StockOrderListItem[] = null;
    public filters: StockOrderListFilters = defaultStockOrderFilters();
    public dataSortKey: string = null;
    public dataSortAscending: boolean = true;
    public pageData: PageData = {
      totalPages: null,
      totalItems: null,
      currentPage: 1,
      pageSize: 50
    };
    public fields: Array<TableField> = [
        { key: "selected", label: "", hidden: false },
        { key: "crn", sortable: true, label: "CRN", hidden: false },
        { key: "facilityCode", sortable: true, label: "Facility", hidden: false },
        { key: "company.companyName", sortable: true, label: "Company", hidden: false },
        { key: "displayBillToCompanyId", sortable: true, label: "BillTo", hidden: false },
        { key: "statusDescription", sortable: true, label: "Status", hidden: false},
        { key: "shipDate", sortable: true, label: "Ship Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "anticipatedReceiveDate", sortable: true, label: "Anticipated Receive Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "receivedDate", sortable: true, label: "Received Date", formatter: formatDate, hidden: false, noWrap: true},
        { key: "locked", sortable: true, label: "Locked By", hidden: false},
    ];
    public loading: boolean = false;

    @Action
    async getStockOrders(params: GetStockOrdersRequest): Promise<void> {
      const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
      const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

      if (pageNumber === 1) {
        this.resetList();
      }
  
      let target = `/barcode/stockOrders/?page=${pageNumber}&PageSize=${pageSize}`;
  
      if (this.filters) {
        if (this.filters.id) {
          target += `&id=${this.filters.id}`;
        }
        if (this.filters.crn) {
            target += `&crn=${this.filters.crn}`;
        }
        if (this.filters.statuses) {
          this.filters.statuses.forEach(element => {
            target += `&statuses=${element}`;
          });
        }
        if (this.filters.facilityCodes) {
          this.filters.facilityCodes.forEach(element => {
            target += `&facilityCodes=${element}`;
          });
        }
        else if(barcodeSystemModuleState.currentFacilityCode)
        {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
        }
        if (this.filters.billToCompanyIds) {
          this.filters.billToCompanyIds.forEach(element => {
            target += `&billToCompanyIds=${element}`;
          });
        }
        if (this.filters.companyIds) {
          this.filters.companyIds.forEach(element => {
            target += `&companyIds=${element}`;
          });
        }
        if (this.filters.isOpen !== undefined && this.filters.isOpen != null) {
          target += `&isOpen=${this.filters.isOpen}`;
        }
        if (this.filters.isLocked !== undefined && this.filters.isLocked != null) {
          target += `&isLocked=${this.filters.isLocked}`;
        }
        if (this.filters.requiresReceiving !== undefined && this.filters.requiresReceiving != null) {
          target += `&requiresReceiving=${this.filters.requiresReceiving}`;
        }
        if (this.filters.receivedDate) {
            const dateData = this.filters.receivedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivedDateStart=${dateData[0]}&receivedDateEnd=${dateData[1]}`;
        }
        if (this.filters.shipDate) {
            const dateData = this.filters.shipDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&shipDateStart=${dateData[0]}&shipDateEnd=${dateData[1]}`;
        }
        if (this.filters.anticipatedReceiveDate) {
            const dateData = this.filters.anticipatedReceiveDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&anticipatedReceiveDateStart=${dateData[0]}&anticipatedReceiveDateEnd=${dateData[1]}`;
        }
        if (this.filters.receivingProcessedDate) {
            const dateData = this.filters.receivingProcessedDate.split(" ~ ");
            if(!dateData[1] || dateData[1] === "null")
            {
                dateData[1] = dateData[0];
            }
            target += `&receivingProcessedDateStart=${dateData[0]}&receivingProcessedDateEnd=${dateData[1]}`;
        }
        if (this.filters.shippingFacility) {
            target += `&shippingFacility=${this.filters.shippingFacility}`;
        }
      }
      else if(barcodeSystemModuleState.currentFacilityCode)
      {
          target += `&facilityCodes=${barcodeSystemModuleState.currentFacilityCode}`;
      }

      if (this.dataSortKey) {
        target += `&sortField=${this.dataSortKey}&SortAscending=${
          this.dataSortAscending ? "true" : "false"
        }`;
      }
  
      try {
        var response = await rawApi.get<StockOrderListItem[]>(target);
        this.setList(response);
        this.setCurrentPage(pageNumber);
      } catch (ex) {
        throw ex;
      } finally {
        this.setLoading(false);
      }
    }

    @Action
    async applyListSort(sort: string) {     
      this.setDataSort(sort);
      await this.getStockOrders({
          pageNumber: 1,
          pageSize: this.pageData.pageSize
      });
    }

    @Action
    toggleField(fieldChange) {
      this.setFieldStatus(fieldChange);
    }

    @Action
    async getStockOrder(id): Promise<StockOrderResource> {
      const response = await rawApi.get<StockOrderResource>(`/barcode/stockOrders/${id}`);
      return response.data;
    }

    @Action
    async getStockOrderItems(id): Promise<StockOrderItemListItem[]> {
      const response = await rawApi.get<StockOrderItemListItem[]>(`/barcode/stockOrders/${id}/stockOrderItems`);
      return response.data;
    }
 
    @Action
    async previewReceiveStockOrder(id): Promise<ReceiveStockOrderRequest> {
      const response = await rawApi.get<ReceiveStockOrderRequest>(`/barcode/stockOrders/${id}/receive`);
      return response.data;
    }

    @Action
    async receiveStockOrder(request: ReceiveStockOrderRequest) {
      await api.postAsync(`barcode/stockOrders/${request.id}/receive`, request);
    } 

    @Action
    async cancelStockOrder(stockOrder: any) {
      await api.postAsync(`barcode/stockOrders/${stockOrder.id}/cancel`, stockOrder);
    } 

    @Action
    async unlockStockOrder(stockOrder: any) {
      await api.postAsync(`barcode/stockOrders/${stockOrder.id}/unlock`, stockOrder);
    } 

    @Action
    async createStockOrder(request: CreateStockOrderRequest) {
      const response = await api.postAsync("barcode/stockOrders", request);
      return response.data;
    }

    @Action 
    resetData() {
        this.resetList();
    }

    @Action
    async loadStockOrder(id: number): Promise<void> {
      const data = await this.getStockOrder(id);
      this.setCurrentStockOrder(data);
      this.refreshStockOrderData(data);
    }   
     
    @Action
    async loadStockOrderItems(id: number): Promise<void> {
      this.setCurrentStockOrderItems(await this.getStockOrderItems(id));
    } 
     
    @Action
    async reloadStockOrder(): Promise<void> {
      if (this.currentStockOrder)
      {
        const data = await this.getStockOrder(this.currentStockOrder.id);
        this.setCurrentStockOrder(data);
        this.refreshStockOrderData(data);
      } 
    }

    @Action
    async viewStockOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.stockOrderViewModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }    
    
    @Action
    async receiveStockOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.receiveStockOrderModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }   
    
    @Action
    async cancelStockOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.cancelStockOrderModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    } 
    
    @Action
    async unlockStockOrderShortcutAction(payload) {
      if (!payload || !payload.selectedItems) {
        return;
      }
      return this.context.dispatch(
        "system/showModal",
        { name: modals.unlockStockOrderModal, data: { selectedItems: payload.selectedItems } },
        { root: true }
      );
    }
    
    @Action
    async updateStockOrdersAfterAction(items: any) {
      for (let index = 0; index < items.length; index++) {
        const order = await this.getStockOrder(items[index].id);
        this.refreshStockOrderData(order)
      }
    }

    @Mutation
    setLoading(isLoading: boolean) {
        this.loading = isLoading;
    }

    @Mutation
    setFilters(filters: StockOrderListFilters)
    {
        this.filters = filters;
    }

    @Mutation
    setList(response: AxiosResponse<StockOrderListItem[]>) {
        this.pageData.totalItems = response.headers.totalitems;
        this.pageData.totalPages = response.headers.totalpages;
        if (this.data) {
            this.data = this.data.concat(response.data);
        } else {
            this.data = response.data;
        }
    }  
    
    @Mutation
    setCurrentPage(number: number) {
      this.pageData.currentPage = number;
    }

    @Mutation
    resetList() {
      this.pageData.totalItems = 0;
      this.pageData.totalPages = 0;
      this.data = null;
    }

    @Mutation
    switchSelectionStatus(id: number) {
      var index = this.data.findIndex(function(item) {
          return item.id === id;
      });

      if (index < 0) {
          return;
      }

      const existing = this.data[index];

      existing.selected = !existing.selected;

      this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
    }  

    @Mutation
    switchStockOrderItemSelectionStatus(id: number) {
      if(this.currentStockOrderItems)
      {
        var index = this.currentStockOrderItems.findIndex(function(item) {
          return item.id === id;
        });

        if (index < 0) {
            return;
        }

        const existing = this.currentStockOrderItems[index];

        existing.selected = !existing.selected;

        this.currentStockOrderItems = [...this.currentStockOrderItems.slice(0, index), existing, ...this.currentStockOrderItems.slice(index + 1)];
      }    
    }

    @Mutation
    refreshStockOrderData(stockOrder: StockOrderListItem) {
      if(this.data)
      {
        var index = this.data.findIndex(function(item) {
            return item.id === stockOrder.id;
        });

        if (index < 0) {
            return;
        }

        this.data = [...this.data.slice(0, index), stockOrder, ...this.data.slice(index + 1)]
      }
    } 
    
    @Mutation
    setCurrentStockOrder(data) {
      this.loading = false;
      this.currentStockOrderItems = [];
      this.currentStockOrder = data;
    }    
    
    @Mutation
    setCurrentStockOrderItems(data) {
      this.loading = false;
      this.currentStockOrderItems = data;
    } 
    

    @Mutation
    resetCurrentBarcodeOrder() {
      this.currentStockOrder = defaultStockOrder();
      this.currentStockOrderItems = [];
    }

    @Mutation
    setFieldStatus(loadFieldChange) {
      this.fields[loadFieldChange.idx].hidden = loadFieldChange.value;
    }

    @Mutation
    setDataSort(key) {
      if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
      else {
        this.dataSortKey = key;
        this.dataSortAscending = true;
      }
    }
    

    get getSelectedItems(): Array<StockOrderListItem> {
        if (!this.data) return [];
        return this.data.filter(x => x.selected);
    }

    get getSelectedItemCount(): number {
        return this.getSelectedItems.length;
    }

    get getSelectedStockOrderItems(): Array<StockOrderItemListItem> {
      if (!this.currentStockOrderItems) return [];
      return this.currentStockOrderItems.filter(x => x.selected);
    }

    get getSelectedStockOrderItemCount(): number {
      return this.getSelectedStockOrderItems.length;
    }
}